import { resolveAuthContext } from './authContext';
import { AccountPracInfoStorage } from '../../storage/AccountPracInfoStorage';
import { AccountPracInfoService } from '../account/AccountPracInfoService';

// Same globalThis-backed in-flight registry ProGroupsShell.tsx / ProGroupsAccountView.tsx use,
// so a fetch kicked off here doesn't race a duplicate one from the Account tab.
const isAccountFetchInFlight = (accountId: string): boolean => {
  const scope = globalThis as any;
  return !!scope.__proGroupsAccountPracInfoInFlightByAccountId?.[accountId];
};
const setAccountFetchInFlight = (accountId: string, value: boolean): void => {
  const scope = globalThis as any;
  if (!scope.__proGroupsAccountPracInfoInFlightByAccountId) {
    scope.__proGroupsAccountPracInfoInFlightByAccountId = {};
  }
  scope.__proGroupsAccountPracInfoInFlightByAccountId[accountId] = value;
};

/**
 * The account/family id resolution used anywhere the app needs "which account is this session
 * acting for" — ProGroupsShell.tsx's account-settings bootstrap (main menu header branding,
 * PracInfo/SkinType lookups, etc.) and ProGroupsWeb.tsx's inline report view (skin header) both
 * need this. Originally each had its own private copy; ProGroupsWeb.tsx's copy silently dropped
 * several of the fallback fields below (only checked familyId, not accountId/AccountId), so a
 * session shaped without familyId resolved fine in ProGroupsShell but failed here — the report
 * view's skin header intermittently reverted to the ChildNav default. Sharing one implementation
 * is what actually prevents that kind of drift, not just a synced first copy-paste.
 */

const wait = (ms: number): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

/** Synchronous, best-effort read straight off the in-memory userStore session. */
export const resolveAccountId = (): string | null => {
  const userStore = resolveAuthContext().userStore;
  const state = userStore?.getState ? userStore.getState() : null;
  const session = state?.session;
  const user = session?.user as Record<string, unknown> | undefined;
  const familyAccount = session?.familyAccount as Record<string, unknown> | undefined;

  const value =
    (typeof user?.familyId === 'string' ? user.familyId : null)
    || (typeof user?.accountId === 'string' ? user.accountId : null)
    || (typeof user?.AccountId === 'string' ? user.AccountId : null)
    || (typeof familyAccount?.familyId === 'string' ? familyAccount.familyId : null)
    || (typeof familyAccount?.accountId === 'string' ? familyAccount.accountId : null)
    || (typeof familyAccount?.AccountId === 'string' ? familyAccount.AccountId : null)
    || null;

  if (value && value.trim().length > 0) {
    return value;
  }

  return null;
};

/** Fallback for when the userStore hasn't hydrated yet — reads the raw persisted session. */
export const resolveStoredSessionAccountId = async (storage?: any): Promise<string | null> => {
  if (!storage?.getItem) {
    return null;
  }

  try {
    const raw = await storage.getItem('userSession');
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as any;
    const value =
      parsed?.user?.familyId
      || parsed?.user?.accountId
      || parsed?.user?.AccountId
      || parsed?.familyAccount?.familyId
      || parsed?.familyAccount?.accountId
      || parsed?.familyAccount?.AccountId
      || null;

    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  } catch (error) {
    console.warn('resolveAccountId: failed to resolve account id from stored session', error);
  }

  return null;
};

/**
 * Retries both sources above for up to ~3s (20 attempts, 150ms apart) — the userStore session
 * and the persisted-storage fallback can both still be hydrating for a moment right after
 * sign-in/app load, and a single one-shot check right then can lose that race and resolve null
 * even though the real accountId lands a beat later. Prefer this over a bare resolveAccountId()
 * call in any effect that runs at/near mount.
 */
export const resolveAccountIdWithRetry = async (storage?: any): Promise<string | null> => {
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const fromStore = resolveAccountId();
    if (fromStore) {
      return fromStore;
    }

    const fromStorage = await resolveStoredSessionAccountId(storage);
    if (fromStorage) {
      return fromStorage;
    }

    await wait(150);
  }

  return null;
};

const skinTypeFromCache = (cached: any): string | null => {
  const skinType = cached?.AccountSettings?.PracInfo?.SkinType;
  return typeof skinType === 'string' && skinType.trim().length > 0 ? skinType.trim() : null;
};

/**
 * Fetches AccountSettings for `accountId` and merge-writes it into AccountPracInfoStorage — the
 * same fetch+merge-cache ProGroupsShell / ProGroupsAccountView do, but callable from the Clients
 * tab, where neither of those runs. Guarded by the shared in-flight registry. Returns the
 * account's SkinType from the response, or null.
 */
const fetchAndCacheAccountSettings = async (
  accountId: string,
  storage: any,
  apiService: any,
): Promise<string | null> => {
  if (isAccountFetchInFlight(accountId)) return null;
  setAccountFetchInFlight(accountId, true);
  try {
    const dto = await new AccountPracInfoService(apiService).getAccountPracInfo(
      { AccountId: accountId, Types: ['AccountSettings'] },
      { userStore: resolveAuthContext().userStore, storage },
    );
    const store = new AccountPracInfoStorage(storage);
    const existing = await store.getAccountPracInfo(accountId).catch(() => null);
    // Merge (not replace) so we don't drop other Types another writer may have cached.
    await store.saveAccountPracInfo(accountId, existing ? { ...existing, ...dto } : dto);
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('progroups:account-data-loaded', { detail: { accountId } }));
    }
    return skinTypeFromCache(dto);
  } catch (error) {
    console.warn('resolveAccountId: AccountSettings fetch failed', error);
    return null;
  } finally {
    setAccountFetchInFlight(accountId, false);
  }
};

/**
 * Resolves the active account's PracInfo.SkinType.
 *
 * Two things it depends on both land asynchronously on a cold load and there is no single
 * reliable "ready" event: the userStore session (accountId) and the AccountPracInfoStorage
 * cache (`account_prac_info_<id>`, holds SkinType). Worse, on a cache-wiped start that lands on
 * the Clients tab, *nothing* fetches AccountPracInfo — ProGroupsShell's bootstrap only polls
 * then falls back to defaults, and the Account tab (which does fetch) isn't mounted. So this:
 *   1. retries the accountId lookup + cache read (`maxAttempts` × `intervalMs`), and
 *   2. once `apiService` is given and the cache still hasn't produced a SkinType after a few
 *      tries, fetches AccountSettings itself and caches it.
 * `shouldCancel` lets a React effect abort on unmount.
 */
export const resolveAccountSkinTypeWithRetry = async (
  storage?: any,
  options?: {
    shouldCancel?: () => boolean;
    maxAttempts?: number;
    intervalMs?: number;
    apiService?: any;
  },
): Promise<string | null> => {
  const shouldCancel = options?.shouldCancel;
  const maxAttempts = options?.maxAttempts ?? 80;
  const intervalMs = options?.intervalMs ?? 250;
  let fetchTried = false;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (shouldCancel?.()) return null;

    const accountId = resolveAccountId() ?? await resolveStoredSessionAccountId(storage);
    if (accountId && storage) {
      try {
        const cached = await new AccountPracInfoStorage(storage).getAccountPracInfo(accountId);
        const fromCache = skinTypeFromCache(cached);
        if (fromCache) {
          return fromCache;
        }
      } catch {
        // fall through and retry / fetch
      }

      // Cache hasn't produced a SkinType — fetch it ourselves (once).
      if (!fetchTried && options?.apiService && attempt >= 2) {
        fetchTried = true;
        const fetched = await fetchAndCacheAccountSettings(accountId, storage, options.apiService);
        if (shouldCancel?.()) return null;
        if (fetched) {
          return fetched;
        }
      }
    }

    if (attempt < maxAttempts - 1) {
      await wait(intervalMs);
    }
  }

  return null;
};
